import { ratesInterface } from '../interfaces';
import { MainProductType, Product } from '../modules/ShoppingCart/interfaces';
import {
  calcDpTotalUtil,
  convertCurrencyUtil,
  DpPricePart,
  DpTotalResult,
  hasSameCurrency,
  normalizeCurrencySign,
} from './currencyConversion';

type PaxCounts = { adult?: number; child?: number; infant?: number };

const getFlightLegPrice = (
  flightInfo: {
    price?: {
      adult?: number;
      child?: number;
      infant?: number;
      total?: number;
      grandTotal?: number;
      currency_sign?: string;
    };
    flightFrom?: { baggage?: { suitcase_price?: number; trolly_price?: number } };
    flightBack?: { baggage?: { suitcase_price?: number; trolly_price?: number } };
  },
  pax: PaxCounts
): number => {
  const suitcasePrice =
    flightInfo?.flightFrom?.baggage?.suitcase_price ||
    flightInfo?.flightBack?.baggage?.suitcase_price ||
    0;
  const trollyPrice =
    flightInfo?.flightFrom?.baggage?.trolly_price ||
    flightInfo?.flightBack?.baggage?.trolly_price ||
    0;

  const calculatedTotalPrice =
    Number(pax.adult ?? 0) * (flightInfo?.price?.adult ?? 0) +
    Number(pax.child ?? 0) * (flightInfo?.price?.child ?? 0) +
    Number(pax.infant ?? 0) * (flightInfo?.price?.infant ?? 0) +
    suitcasePrice +
    trollyPrice;

  return (
    flightInfo?.price?.grandTotal ??
    flightInfo?.price?.total ??
    calculatedTotalPrice
  );
};

/** Currency sign a cart line (product or sub-product) is priced in. */
export const getCartLineCurrency = (
  product: Product,
  cartCurrencySign?: string
): string | undefined => {
  if (product.base_currency_sign) {
    return product.base_currency_sign;
  }

  const data = product.product_data;

  if (product.type === MainProductType.DynamicPackage) {
    return data?.currency_sign ?? cartCurrencySign;
  }

  if (
    product.type === MainProductType.oneWayFlight ||
    product.type === MainProductType.Flight
  ) {
    return (
      data?.singleFlight?.flightsInfo?.price?.currency_sign ??
      data?.price?.currency_sign ??
      data?.flightsInfo?.price?.currency_sign
    );
  }

  if (product.type === MainProductType.Event || product.type === MainProductType.EventTicket) {
    return data?.currency ?? data?.ticketDetails?.currency ?? cartCurrencySign;
  }

  if (product.type === MainProductType.Room) {
    return data?.currency_sign ?? cartCurrencySign;
  }

  if (product.type === MainProductType.Hotel) {
    return (
      data?.currency_sign ??
      data?.pricingInfo?.currency_sign ??
      cartCurrencySign
    );
  }

  if (
    product.type === MainProductType.Flights ||
    product.type === MainProductType.oneWayFlights
  ) {
    return data?.flightsInfo?.price?.currency_sign ?? cartCurrencySign;
  }

  return (
    data?.price?.currency_sign ??
    data?.flightsInfo?.price?.currency_sign ??
    data?.currency_sign ??
    cartCurrencySign
  );
};

const pushLinePart = (
  parts: DpPricePart[],
  amount: number | undefined,
  currency: string | undefined
) => {
  if (!Number.isFinite(Number(amount))) {
    return;
  }
  parts.push({ amount: Number(amount), currency });
};

/** Billable cart lines for gross or net totals (one entry per priced row). */
export const collectCartBillableParts = (
  products: Product[] | undefined,
  cartCurrencySign: string | undefined,
  field: 'total_price' | 'total_net_price'
): DpPricePart[] => {
  const parts: DpPricePart[] = [];

  products?.forEach((product) => {
    const lineAmount = product[field];

    if (lineAmount > 0) {
      pushLinePart(
        parts,
        lineAmount,
        getCartLineCurrency(product, cartCurrencySign)
      );
      return;
    }

    product.subProducts?.forEach((subProduct) => {
      const subAmount = subProduct[field];
      if (subAmount > 0) {
        pushLinePart(
          parts,
          subAmount,
          getCartLineCurrency(subProduct, cartCurrencySign)
        );
      }
    });
  });

  return parts;
};

export const calculateCartTotalsUtil = ({
  products,
  cartCurrencySign,
  rates,
}: {
  products: Product[] | undefined;
  cartCurrencySign?: string;
  rates?: ratesInterface;
}): {
  gross: DpTotalResult;
  net: DpTotalResult;
} => {
  const grossParts = collectCartBillableParts(
    products,
    cartCurrencySign,
    'total_price'
  );
  const netParts = collectCartBillableParts(
    products,
    cartCurrencySign,
    'total_net_price'
  );

  if (!rates || grossParts.length === 0) {
    const grossRaw = grossParts.reduce(
      (sum, part) => sum + Number(part.amount),
      0
    );
    const netRaw = netParts.reduce((sum, part) => sum + Number(part.amount), 0);
    const fallbackCurrency = cartCurrencySign ?? '$';
    return {
      gross: { total: Math.ceil(grossRaw), currency: fallbackCurrency },
      net: { total: Math.ceil(netRaw), currency: fallbackCurrency },
    };
  }

  return {
    gross: calcDpTotalUtil({ parts: grossParts, rates }),
    net: calcDpTotalUtil({ parts: netParts, rates }),
  };
};

/** Combined total for multiple flight legs (search / view / cart). */
export const calcFlightsTotalUtil = ({
  flights,
  pax,
  rates,
}: {
  flights: Array<{
    price?: {
      adult?: number;
      child?: number;
      infant?: number;
      total?: number;
      grandTotal?: number;
      currency_sign?: string;
    };
    flightFrom?: { baggage?: { suitcase_price?: number; trolly_price?: number } };
    flightBack?: { baggage?: { suitcase_price?: number; trolly_price?: number } };
  }>;
  pax: PaxCounts;
  rates: ratesInterface;
}): DpTotalResult => {
  const parts: DpPricePart[] = flights.map((flight) => ({
    amount: getFlightLegPrice(flight, pax),
    currency: flight.price?.currency_sign,
  }));

  return calcDpTotalUtil({ parts, rates });
};

export interface OneWayPerPaxLeg {
  /** Currency sign the leg is priced in (from base_currency_sign / flight price). */
  currency?: string;
  /** Raw per-leg share — (per_pax × pax / N) — exactly as the BE adds it, in the
   *  leg's own currency, with NO conversion (mirrors handleOneWayPerPaxRevenue). */
  rawShare: number;
  /** rawShare converted from the leg currency into targetCurrency (full precision). */
  converted: number;
}

export interface OneWayPerPaxCommission {
  legs: OneWayPerPaxLeg[];
  /** Σ of every leg's converted share, in targetCurrency. Full precision —
   *  the caller applies the single Math.ceil. */
  totalConverted: number;
  /** True when the legs span ≥2 distinct (normalized) currencies — the only case
   *  where the per-leg split visibly changes the displayed number. */
  isMixedCurrency: boolean;
  /** The shared raw share applied to every leg (per_pax × pax / N). */
  rawSharePerLeg: number;
}

/**
 * Currency-aware per-pax commission for a one-way-flights product, mirroring the
 * BE (`CoreService.handleOneWayPerPaxRevenue`): the total commission
 * `per_pax × pax` is split EQUALLY across the N legs and each leg's share is added
 * to that leg as a RAW number in the leg's own currency (the BE never converts
 * `per_pax` into the leg currency). To display a number that matches the BE after
 * refresh-rates, we replicate that and then convert each leg's raw share into the
 * target display currency.
 *
 * `pax` is the product's adult+child count (== BE `data.pax`); `N` is the leg count
 * (== BE `product_data.flights_amount`, set to `subProducts.length`).
 */
export const computeOneWayPerPaxCommission = ({
  product,
  perPax,
  pax,
  rates,
  targetCurrency,
  cartCurrencySign,
}: {
  product: Product;
  perPax: number;
  pax: number;
  rates?: ratesInterface;
  targetCurrency: string;
  cartCurrencySign?: string;
}): OneWayPerPaxCommission => {
  const legProducts = product.subProducts ?? [];
  const legCount = legProducts.length;
  const rawSharePerLeg =
    legCount > 0 ? (Number(perPax) * Number(pax)) / legCount : 0;

  const legs: OneWayPerPaxLeg[] = legProducts.map((leg) => {
    const currency = getCartLineCurrency(leg, cartCurrencySign);
    const converted = rates
      ? convertCurrencyUtil({
          amount: rawSharePerLeg,
          from: currency,
          to: targetCurrency,
          rates,
        })
      : rawSharePerLeg;
    return { currency, rawShare: rawSharePerLeg, converted };
  });

  const totalConverted = legs.reduce((sum, leg) => sum + leg.converted, 0);

  const distinctSigns = new Set(
    legs
      .map((leg) => normalizeCurrencySign(leg.currency))
      .filter((sign): sign is string => !!sign)
  );

  return {
    legs,
    totalConverted,
    isMixedCurrency: distinctSigns.size > 1,
    rawSharePerLeg,
  };
};

export interface DynamicPackagePerPaxPortion {
  /** 'flight' for the bundle-flight sub-line, 'room' for each room sub-line. */
  kind: 'flight' | 'room';
  /** Currency sign the portion is priced in (flight vs. room may differ). */
  currency?: string;
  /** Raw portion the BE adds to that sub-line in its own currency, unconverted. */
  rawShare: number;
  /** rawShare converted into targetCurrency (full precision). */
  converted: number;
}

export interface DynamicPackagePerPaxCommission {
  portions: DynamicPackagePerPaxPortion[];
  /** Σ of every portion's converted value, in targetCurrency (full precision). */
  totalConverted: number;
  /** True when the contributing sub-lines span ≥2 distinct currencies. */
  isMixedCurrency: boolean;
}

/**
 * Currency-aware per-pax commission for a dynamic package, mirroring the BE
 * (`OrderService.recalculateDynamicPackageGrossPrices`): the total commission
 * `per_pax × pax` is split 80% to the bundle flight and 20% across the rooms
 * (equally), each portion added RAW in that sub-line's own currency. We replicate
 * the split and convert each portion into the target currency so the package's
 * aggregate matches the BE total after refresh-rates.
 *
 * `pax` is the package's adult+child count (== BE flight.related_guests count).
 * When the package has no rooms the 20% room portion is dropped, exactly as the
 * BE does (roomCommissionEach = 0 when rooms.length === 0).
 */
export const computeDynamicPackagePerPaxCommission = ({
  product,
  perPax,
  pax,
  rates,
  targetCurrency,
  cartCurrencySign,
}: {
  product: Product;
  perPax: number;
  pax: number;
  rates?: ratesInterface;
  targetCurrency: string;
  cartCurrencySign?: string;
}): DynamicPackagePerPaxCommission => {
  const subs = product.subProducts ?? [];
  const flightSub = subs.find((sp) => sp.type === MainProductType.Flights);
  const rooms = subs.filter((sp) => sp.type === MainProductType.Room);

  const commission = Number(perPax) * Number(pax);

  const portions: DynamicPackagePerPaxPortion[] = [];
  if (flightSub) {
    portions.push({
      kind: 'flight',
      currency: getCartLineCurrency(flightSub, cartCurrencySign),
      rawShare: commission * 0.8,
      converted: 0,
    });
  }
  const roomShareEach = rooms.length ? (commission * 0.2) / rooms.length : 0;
  rooms.forEach((room) => {
    portions.push({
      kind: 'room',
      currency: getCartLineCurrency(room, cartCurrencySign),
      rawShare: roomShareEach,
      converted: 0,
    });
  });

  portions.forEach((portion) => {
    portion.converted = rates
      ? convertCurrencyUtil({
          amount: portion.rawShare,
          from: portion.currency,
          to: targetCurrency,
          rates,
        })
      : portion.rawShare;
  });

  const totalConverted = portions.reduce(
    (sum, portion) => sum + portion.converted,
    0
  );

  const distinctSigns = new Set(
    portions
      .map((portion) => normalizeCurrencySign(portion.currency))
      .filter((sign): sign is string => !!sign)
  );

  return {
    portions,
    totalConverted,
    isMixedCurrency: distinctSigns.size > 1,
  };
};

export const flightsHaveMixedCurrencies = (
  flights: Array<{ price?: { currency_sign?: string } }>
): boolean => {
  if (!flights?.length) {
    return false;
  }
  const first = flights[0]?.price?.currency_sign;
  if (!first) {
    return false;
  }
  return !flights.every((flight) =>
    hasSameCurrency(flight?.price?.currency_sign ?? '', first)
  );
};
